/**
 * CRM product section for partner pages.
 * Shows a 6-feature grid (Leads, Quotes, Scheduling, Jobs, Invoices, Payments)
 * with brand illustration icons and a dashboard screenshot.
 * Used when productType is "CRM".
 *
 * Does not handle data fetching or routing logic.
 * Depends on: ProductSectionLayout, next/image.
 */
import Image from "next/image";

import ProductSectionLayout from "@/components/partners/ProductSectionLayout";

const CRM_FEATURES: { title: string; description: string; icon: string; iconClassName?: string }[] = [
  {
    title: "Leads",
    description: "Capture, organize, and respond to every lead quickly — before competitors do.",
    icon: "/crm-icons/leads.svg",
    iconClassName: "rotate-90 -scale-y-100",
  },
  {
    title: "Quotes",
    description: "Send professional estimates customers can approve instantly.",
    icon: "/crm-icons/quotes.svg",
  },
  {
    title: "Scheduling",
    description: "Keep your calendar organized and your team aligned.",
    icon: "/crm-icons/scheduling.svg",
  },
  {
    title: "Jobs",
    description: "Manage all your jobs and make changes easily.",
    icon: "/crm-icons/jobs.svg",
  },
  {
    title: "Invoices",
    description: "Invoice customers and collect payments without chasing paperwork.",
    icon: "/crm-icons/invoices.svg",
  },
  {
    title: "Payments",
    description: "Get paid faster with online payments and automated reminders.",
    icon: "/crm-icons/payments.svg",
  },
];

export default function CRMProductSection() {
  return (
    <ProductSectionLayout
      badge={<span className="text-rebolt-500 text-xs font-bold uppercase tracking-[1px]">Dashboard</span>}
      heading="Track everything at once and act when customers need attention."
      description={<>Rebolt gives you a complete view of your leads, jobs,<br />and customer conversations so nothing slips through the cracks.</>}
    >
      {/* Feature grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 w-full py-8 md:py-12">
        {CRM_FEATURES.map((feature) => (
          <div key={feature.title} className="flex flex-col items-center gap-4 p-5 text-center">
            <div className="w-20 h-20 flex items-center justify-center">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={feature.icon}
                alt={feature.title}
                className={`w-full h-full object-contain ${feature.iconClassName || ""}`}
              />
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="text-ash-200 text-xl font-semibold leading-tight">{feature.title}</h3>
              <p className="text-ash-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Dashboard screenshot */}
      <div className="w-full mt-auto">
        <div className="relative w-full aspect-[2/1]">
          <Image src="/crm-dashboard.png" alt="Rebolt CRM Dashboard" fill className="object-contain object-bottom" />
        </div>
      </div>
    </ProductSectionLayout>
  );
}
